import React, { memo } from 'react';
import { Button, Modal } from 'gatorcomponents';
import * as Styles from './RebootModal.styles';
import useLocale from '@/hooks/useLocale';
import { shallowEqual, useSelector } from 'react-redux';
import { useResize } from '@/hooks/useResize';

const REBOOT_MODAL_MODE = {
  REBOOT: 'reboot',
  HISTORY: 'rebootHistory',
};

const RebootModal = ({
  mode = REBOOT_MODAL_MODE.REBOOT,
  onClose,
  onConfirm,
  onCheckRebootHistory,
  onGoBack,
}) => {
  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;
  const rebootHistoryLocale = locale && locale.myPlan && locale.myPlan.rebootHistory;
  const { productName } = useSelector(state => ({
    productName: state.vpsOci?.data?.productName,
  }), shallowEqual);
  const history = useSelector(state => state.productDetail?.history, shallowEqual) || [];

  const isHistoryMode = mode === REBOOT_MODAL_MODE.HISTORY;

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 600;

  const listHistoryTimes = history.flatMap((item) => {
    if (!item || !Array.isArray(item.time)) return [];

    const { id, date } = item;
    return item.time.map(time => ({ id, date, time }));
  });

  return (
    <Modal
      onClose={onClose}
      maxWidth={isHistoryMode ? 500 : 800}
      title={isHistoryMode ? rebootHistoryLocale.title : locale.myPlan.rebootModal.title}
      testId={isHistoryMode ? 'reboot-history-modal' : 'reboot-modal'}
    >
      <Styles.Container>
        {isHistoryMode ? (
          <>
            <Styles.Message>{rebootHistoryLocale.message(productName)}</Styles.Message>
            <Styles.RebootInfo>
              {listHistoryTimes.map(item => (
                <Styles.InfoRow key={`${item.id}-${item.time}`} data-testid="reboot-history-modal-info-row">
                  <Styles.Info>
                    <Styles.InfoName>{rebootHistoryLocale.date}</Styles.InfoName>
                    <Styles.InfoValue>{item.date}</Styles.InfoValue>
                  </Styles.Info>
                  <Styles.Info>
                    <Styles.InfoName>{rebootHistoryLocale.time}</Styles.InfoName>
                    <Styles.InfoValue>{item.time}</Styles.InfoValue>
                  </Styles.Info>
                </Styles.InfoRow>
              ))}
            </Styles.RebootInfo>
            <Styles.HistoryActions>
              <Button
                label={rebootHistoryLocale.goBack}
                variant="tertiary"
                size="large"
                version="v2"
                onClick={onGoBack}
                width="fit-content"
                testId="reboot-history-modal-close-button"
              />
            </Styles.HistoryActions>
          </>
        ) : (
          <>
            <Styles.Message data-testid="reboot-modal-message">
              {locale.myPlan.rebootModal.message(productName)}
            </Styles.Message>
            <Styles.Actions>
              <Button
                label={locale.myPlan.rebootModal.cancel}
                variant="tertiary"
                size="large"
                version="v2"
                onClick={onClose}
                width={isMobile ? 'full' : 'fit-content'}
                testId="reboot-modal-cancel-button"
              />
              <Button
                label={locale.myPlan.rebootModal.checkRebootHistory}
                variant="secondary"
                size="large"
                version="v2"
                onClick={onCheckRebootHistory}
                width={isMobile ? 'full' : 'fit-content'}
                testId="reboot-modal-check-history-button"
              />
              <Button
                label={locale.myPlan.rebootModal.confirm}
                variant="primary"
                size="large"
                version="v2"
                onClick={onConfirm}
                width={isMobile ? 'full' : 'fit-content'}
                testId="reboot-modal-confirm-button"
              />
            </Styles.Actions>
          </>
        )}
      </Styles.Container>
    </Modal>
  );
};

export default memo(RebootModal);
